import { Empty, Pagination, Tabs } from 'antd';
import CircularProgress from 'components/CircularProgress';
import WithSlideInComponent from 'components/WithSlideInComponent';
import { formatDateTimeForDisplay } from 'constants/days';
import { useQueryParamsAsKeyValuePair } from 'hooks/useQueryParams';
import useSaveScrollPosition from 'hooks/useSaveScrollPosition';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { AppointmentNotaryTypeDisplay, IAppointment } from 'types/appointments';
import { GeneralFunction, IPagination } from 'types/common';
import { ITransactionType } from 'types/services';
import { getNewQueryString, getUsStates } from 'utils/utils';

import AppointmentCard from './AppointmentCard';
import AppointmentSlideIn from './AppointmentSlideIn';
import { AppointmentsTabContainerStyled, EmptyDataContainerStyled, TabsStyled } from './styled';

const { TabPane } = Tabs;
const AppointmentsTabs: React.FC<{
  appointments?: IAppointment[];
  refetch?: GeneralFunction;
  loading?: boolean;
  appointmentTabsKeyByRole: { key: string; label: string }[];
  meta?: IPagination;
  transactionTypesData?: ITransactionType[];
}> = ({ appointments, refetch, loading, appointmentTabsKeyByRole, meta, transactionTypesData }) => {
  const history = useHistory();
  const { location } = history;
  const query = useQueryParamsAsKeyValuePair();
  const [saveCurrentScrollPos, resetToCurrentScrollPos] = useSaveScrollPosition();

  const [selectedAppointment, setSelectedAppointment] = useState<IAppointment>();

  useEffect(() => {
    if (selectedAppointment) {
      setSelectedAppointment((c) => appointments?.find((ap) => ap.id === selectedAppointment?.id) || c);
    }
  }, [appointments]);

  useEffect(() => {
    if (query?.detail && query?.priorityId && appointments?.[0]?.id === query.priorityId) {
      setSelectedAppointment(appointments?.[0]);
    }
  }, [query?.detail, appointments]);

  // const showNotaryModal = (appointment?: IAppointment) => {
  //   setModalVisible('notary');
  //   if (appointment) setSelectedAppointment(appointment);
  // };

  const onAppointmentCardClick = (appointment: IAppointment) => {
    setSelectedAppointment(appointment);
    saveCurrentScrollPos();
  };

  const onSlideInClose = () => {
    setSelectedAppointment(undefined);
    resetToCurrentScrollPos();
    const newQueryString = getNewQueryString(location.search, { detail: undefined });
    history.push({
      pathname: location.pathname,
      search: newQueryString,
    });
  };

  const AppointmentCardsList = useMemo(
    () => (
      <div className='gx-m-0' style={{ padding: '15px', overflow: 'auto' }}>
        {appointments &&
          appointments.map((appointment) => (
            <AppointmentCard
              transactionTypesData={transactionTypesData}
              key={appointment.id}
              onCardClick={onAppointmentCardClick}
              refetch={refetch}
              // showNotaryModal={showNotaryModal}
              appointment={appointment}
            />
          ))}
        {(!appointments || !appointments.length) && (
          <EmptyDataContainerStyled className='empty-data'>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </EmptyDataContainerStyled>
        )}
      </div>
    ),
    [appointments],
  );

  const onPageChange = (page: number) => {
    const newQueryString = getNewQueryString(location.search, { page });
    history.push({
      pathname: location.pathname,
      search: newQueryString,
    });
  };

  const mainContentConverterWhenSlideActive = (appointment: IAppointment) => {
    const appointmentOwner = appointment.transaction?.createdBy;
    const creatorFullName = `${appointmentOwner?.firstName} ${appointmentOwner?.lastName}`;
    const date = moment(appointment.date).format(formatDateTimeForDisplay('|'));
    const duration = `${(appointment.duration || 0) / 3600} h`;
    const { state } = appointment.address;
    return {
      ...appointment,
      clientName: <span className='gx-font-weight-medium'>{creatorFullName}</span>,
      date,
      duration,
      state: getUsStates(state),
    };
  };

  const PaginationComponent = useMemo(
    () =>
      !loading && !!appointments?.length ? (
        <div
          className='gx-flex-column gx-justify-content-end gx-align-items-end gx-h-100 gx-mt-2'
          style={{ paddingTop: 10, paddingLeft: 10, paddingRight: 10 }}>
          <Pagination
            total={meta?.itemCount}
            defaultCurrent={query?.page || 1}
            current={query?.page}
            onChange={onPageChange}
          />
        </div>
      ) : null,
    [loading, !!appointments?.length, selectedAppointment],
  );

  return (
    <div className='gx-h-100 gx-w-100 gx-overflow-y-auto'>
      <>
        <AppointmentsTabContainerStyled>
          <TabsStyled
            defaultActiveKey={query?.tab || appointmentTabsKeyByRole[0]?.key || ''}
            activeKey={query?.tab || appointmentTabsKeyByRole[0]?.key}
            onChange={(key) => {
              const newQuery = getNewQueryString(location.search, { tab: key, page: undefined });
              history.push({
                pathname: location.pathname,
                search: newQuery,
              });
            }}>
            {appointmentTabsKeyByRole.map((tab) => (
              <TabPane tab={tab.label} key={tab.key}>
                {loading ? (
                  <CircularProgress />
                ) : (
                  <WithSlideInComponent
                    slideInStyle={{
                      width: '60%',
                    }}
                    highlightActiveContent
                    activeKey={selectedAppointment?.id}
                    onContentClick={setSelectedAppointment}
                    slideInProps={{
                      children: (
                        <AppointmentSlideIn
                          refetchAppointments={refetch}
                          selectedAppointment={selectedAppointment}
                          // showNotaryModal={showNotaryModal}
                          transactionTypesData={transactionTypesData}
                        />
                      ),
                      onClose: onSlideInClose,
                      borderedTitle: true,
                      title: (
                        <div className='ant-slideIn-title' style={{ paddingTop: 10 }}>
                          Appointment Detail{' '}
                          {selectedAppointment?.type
                            ? `- ${AppointmentNotaryTypeDisplay[selectedAppointment.type]}`
                            : ''}
                        </div>
                      ),
                    }}
                    slideInVisible={!!selectedAppointment}
                    mainContentDataWhenSlideIn={appointments}
                    displayDataKeysWhenSlideIn={['clientName', 'empty', 'state', 'date', 'date']}
                    mainContentDataConverter={mainContentConverterWhenSlideActive}
                    renderExtraWhenSlideIn={PaginationComponent}>
                    <div className='gx-h-100 gx-overflow-y-auto'>
                      {!loading && AppointmentCardsList}
                      {loading && <CircularProgress />}
                      {PaginationComponent && <div>{PaginationComponent}</div>}
                    </div>
                  </WithSlideInComponent>
                )}
              </TabPane>
            ))}
          </TabsStyled>
        </AppointmentsTabContainerStyled>
      </>
    </div>
  );
};

export default React.memo(AppointmentsTabs);
